/**
 * @file finance-delivered-report.tsx
 * @module app/modules/finance-reports/finance-delivered-report
 * @description Displays the finance report for delivered orders, including filters, totals, and progress data.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Card, Col, FormLabel, Row, ProgressBar } from "react-bootstrap";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  CartonWithDozens,
  CartonWithPieces,
  Dozen,
  FinanceDeliveryReportPermission,
  PAGE_LIMIT,
  Piece,
  DeliveryReports,
  Download,
  SellersSalesReport,
} from "../../../utils/constants";
import APICallService from "../../../api/apiCallService";
import { seller } from "../../../api/apiEndPoints";
import Method from "../../../utils/methods";
import Loader from "../../../Global/loader";
import CustomDatePicker from "../../custom/DateRange/DatePicker";
import { useAuth } from "../auth";
import clsx from "clsx";
import { CustomSelect } from "../../custom/Select/CustomSelect";
import { useWarehouse } from "../../context/WarehouseContext";
import React from "react";
import ImageWithFallback from "../_common/components/image-with-fallback";

const FinanceDeliveryReport = () => {
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const [details, setDetails] = useState<any>();
  const [endDate, setEndDate] = useState<any>(new Date());
  const [startDate, setStartDate] = useState<any>(new Date());
  const [fetchLoading, setFetchLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(PAGE_LIMIT);
  const [search, setSearch] = useState<string>("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [paymentData, setPaymentData] = useState<any>([]);
  const [paymentDetails, setPaymentDetails] = useState<any>([]);
  const [productLoading, setProductLoading] = useState(false);
  const [searchProduct, setSearchProduct] = useState("");
  const [totalProducts, setTotalProducts] = useState(0);
  const [selectedBrand, setSelectedBrand] = useState<any>();
  const [selectedDistrict, setSelectedDistrict] = useState<any>();
  const [selectedProduct, setSelectedProduct] = useState<any>();
  const [productsList, setProductsList] = useState<any>([]);
  const [brands, setBrands] = useState<any>([]);
  const [district, setDistricts] = useState<any>([]);
  const [isExporting, setIsExporting] = useState(false);
  const exportRef = React.useRef<number>(0);
  const isAllowedToDownload = Method.hasPermission(
    FinanceDeliveryReportPermission,
    Download,
    currentUser
  );

  useEffect(() => {
    (async () => {
      setFetchLoading(true);
      if (!Method.hasModulePermission(SellersSalesReport, currentUser)) {
        return window.history.back();
      }
      setSelectedBrand(null);
      setSelectedProduct(null);
      setSelectedDistrict(null);
      await fetchOrder(
        startDate,
        endDate,
        selectedBrand ? selectedBrand?._id : null,
        selectedProduct ? selectedProduct?.value : null
      );
      setFetchLoading(false);
    })();
  }, [selectedWarehouse?.value]);

  const fetchOrder = async (
    startDate: string,
    endDate: string,
    brands?: any,
    product?: any,
    district?: any
  ) => {
    let params: any = {
      fromDate: startDate
        ? Method.convertDateToFormat(startDate, "YYYY-MM-DD")
        : "",
      toDate: endDate ? Method.convertDateToFormat(endDate, "YYYY-MM-DD") : "",
    };
    if (brands) {
      params = {
        ...params,
        ["brands[0]"]: brands,
      };
    }
    if (district) {
      params = {
        ...params,
        ["districts[0]"]: district,
      };
    }
    if (product) {
      params = {
        ...params,
        ["variants[0]"]: product,
      };
    }
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      seller.deliveryReport,
      params,
      "",
      "",
      "",
      "",
      FinanceDeliveryReportPermission,
      selectedWarehouse?.value
    );
    let response = await apiService.callAPI();
    if (response) {
      setPaymentData(response);
      getUniqueProducts(response);
      getUniqueBrands(response);
      getUniqueDistricts(response);
    }
  };

  const getUniqueProducts = (data: any) => {
    const uniqueVariants = new Set();
    data.forEach((item: any) => {
      const variant = item?.variant;
      const variantInfo = {
        title: variant?.title,
        id: variant?._id,
        media: variant?.media,
      };
      uniqueVariants?.add(JSON.stringify(variantInfo));
    });
    const uniqueVariantsArray = Array?.from(uniqueVariants)?.map(
      (variant: any) => JSON.parse(variant)
    );
    setProductsList(uniqueVariantsArray);
  };

  const getUniqueBrands = (data: any) => {
    const uniqueVariants = new Set();
    data.forEach((item: any) => {
      const variant = item?.variant?.product?.brand;
      const variantInfo = {
        title: variant?.title,
        id: variant?.reference,
        media: variant?.media,
      };
      uniqueVariants?.add(JSON.stringify(variantInfo));
    });
    const uniqueVariantsArray = Array?.from(uniqueVariants)?.map(
      (variant: any) => JSON.parse(variant)
    );
    setBrands(uniqueVariantsArray);
  };

  const getUniqueDistricts = (data: any) => {
    const uniqueVariants = new Set();
    data?.forEach((item: any) => {
      const variant = item?.district;
      const variantInfo = {
        title: variant?.name,
        id: variant?.id,
      };
      uniqueVariants?.add(JSON.stringify(variantInfo));
    });
    const uniqueVariantsArray = Array?.from(uniqueVariants)?.map(
      (variant: any) => JSON.parse(variant)
    );
    setDistricts(uniqueVariantsArray);
  };

  const handleChange = async ([startDate, endDate]: any) => {
    setStartDate(startDate);
    setEndDate(endDate);
    if (startDate && endDate) {
      setFetchLoading(true);
      setTotalRecords(0);
      await fetchOrder(
        Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
        Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
        selectedBrand ? selectedBrand?.id : null,
        selectedProduct ? selectedProduct?.value : null,
        selectedDistrict ? selectedDistrict?.value : null
      );
      setFetchLoading(false);
    }
  };

  const handleProductSelect = async (event: any) => {
    if (event) {
      setTotalRecords(0);
      setPaymentData([]);
      setFetchLoading(true);
      setSelectedProduct(event);
      setPage(1);
      await fetchOrder(
        Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
        Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
        selectedBrand ? selectedBrand?.id : null,
        event.value,
        selectedDistrict ? selectedDistrict?.id : null
      );
      setFetchLoading(false);
    } else {
      setTotalRecords(0);
      setPaymentData([]);
      setFetchLoading(true);
      setSelectedProduct(null);
      await fetchOrder(
        Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
        Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
        selectedBrand ? selectedBrand?.id : null,
        null,
        selectedDistrict ? selectedDistrict?.id : null
      );
      setFetchLoading(false);
    }
  };

  const onMenuClose = async () => {
    setSearch("");
  };
  const onMenuOpen = async () => {
    setPage(1);
  };

  // --- Summary Calculations ---
  const totalDeliveredInclVAT = paymentData.reduce(
    (acc: number, val: any) => acc + (val?.totalAmount || 0),
    0
  );
  const totalDeliveredExclVAT = paymentData.reduce(
    (acc: number, val: any) => acc + (val?.totalTaxAmount || 0),
    0
  );
  const totalDeliveredVAT = paymentData.reduce(
    (acc: number, val: any) => acc + (val?.tax || 0),
    0
  );

  const totalReturnedInclVAT = paymentData.reduce((acc: number, val: any) => {
    const returnedQty = val?.totalReturnedQuantities || 0;
    return acc + returnedQty * (val?.averagePrice || 0);
  }, 0);
  const totalReturnedExclVAT = paymentData.reduce((acc: number, val: any) => {
    const returnedQty = val?.totalReturnedQuantities || 0;
    return acc + returnedQty * (val?.averageTaxPrice || 0);
  }, 0);
  const totalReturnedVAT = paymentData.reduce((acc: number, val: any) => {
    const returnedQty = val?.totalReturnedQuantities || 0;
    return acc + returnedQty * (val?.taxPerUnit || 0);
  }, 0);

  const netInclVAT = totalDeliveredInclVAT - totalReturnedInclVAT;
  const netExclVAT = totalDeliveredExclVAT - totalReturnedExclVAT;
  const netVAT = totalDeliveredVAT - totalReturnedVAT;

  const exportHeaders = [
    "Product Name",
    "Reference Key",
    "Customer Name",
    "Unit Price (Incl. VAT)",
    "Unit Price (Excl. VAT)",
    "VAT / Unit",
    "Total Quantity",
    "Delivered Quantity",
    "Delivered Total (Incl. VAT)",
    "Delivered Total (Excl. VAT)",
    "Delivered VAT Amount",
  ];

  const rowMapper = (val: any) => {
    const totalQty = val?.totalQuantities || 0;
    const returnedQty = val?.totalReturnedQuantities || 0;
    const deliveredQty =
      val?.totalReturnedQuantities && val?.totalQuantities
        ? val?.totalQuantities - val?.totalReturnedQuantities
        : val?.totalQuantities
        ? val?.totalDelivered
        : 0;

    return [
      val?.variant?.title || "N/A",
      val?.refKey || "N/A",
      val?.name || "N/A",
      val?.averagePrice || 0,
      val?.averageTaxPrice || 0,
      val?.taxPerUnit || 0,
      totalQty,
      deliveredQty,
      val?.totalAmount || 0,
      val?.totalTaxAmount || 0,
      val?.tax || 0,
    ];
  };

  const exportParams = {
    fromDate: startDate
      ? Method.convertDateToFormat(startDate, "YYYY-MM-DD")
      : "",
    toDate: endDate ? Method.convertDateToFormat(endDate, "YYYY-MM-DD") : "",
    ...(selectedBrand && { "brands[0]": selectedBrand.id }),
    ...(selectedProduct && { "variants[0]": selectedProduct.value }),
    ...(selectedDistrict && { "districts[0]": selectedDistrict.value }),
  };

  const handleExportExcel = async () => {
    const runId = ++exportRef.current;
    const toastId = toast.loading("Initializing export...");
    const EXPORT_LIMIT = 10;
    const sanitizedParams: any = { ...exportParams, download: true };
    if (selectedWarehouse?.value)
      sanitizedParams.warehouse = selectedWarehouse.value;

    try {
      setIsExporting(true);
      if (runId !== exportRef.current) {
        toast.dismiss(toastId);
        return;
      }
      toast.update(toastId, {
        render: "Fetching all records...",
        type: "info",
        isLoading: true,
      });

      const apiService = new APICallService(
        seller.deliveryReport,
        sanitizedParams,
        "",
        "",
        "",
        "",
        FinanceDeliveryReportPermission,
        selectedWarehouse?.value
      );
      const response = await apiService.callAPI();

      if (runId !== exportRef.current) return;
      if (!response) throw new Error("Connection lost or API error.");

      const data = Array.isArray(response) ? response : response?.records || [];

      if (data.length === 0) {
        toast.dismiss(toastId);
        toast.error("No records found to export");
        return;
      }

      const workbook = XLSX.utils.book_new();
      const worksheetData: any[] = [exportHeaders];

      toast.update(toastId, {
        render: `Mapping ${data.length} records...`,
        type: "info",
        isLoading: true,
      });
      worksheetData.push(...data.map(rowMapper));

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      worksheet["!cols"] = [
        { width: 35 },
        { width: 15 },
        { width: 20 },
        { width: 20 },
        { width: 15 },
        { width: 15 },
        { width: 15 },
        { width: 20 },
        { width: 20 },
        { width: 15 },
      ];
      XLSX.utils.book_append_sheet(workbook, worksheet, "Delivery Report");
      XLSX.writeFile(
        workbook,
        `Finance-Delivery-Report-${new Date().toISOString().split("T")[0]}.xlsx`
      );
      toast.dismiss(toastId);
      toast.success("Export completed successfully!");
    } catch (error) {
      if (runId === exportRef.current) {
        toast.dismiss(toastId);
        toast.error("Export failed: " + (error as Error).message);
      }
    } finally {
      if (runId === exportRef.current) setIsExporting(false);
    }
  };

  return (
    <div className="p-5">
      {/* Filters Row */}
      <Row className="g-4">
        <Col md>
          <div className="d-flex align-items-center mt-4">
            <h1 className="fs-22 fw-bolder">Delivery Report </h1>
          </div>
        </Col>
        <Col md="auto">
          <div className="d-flex align-items-center">
            <FormLabel className="fs-16 fw-500">Filter by Product</FormLabel>
            <div className="ms-5">
              <CustomSelect
                backgroundColor="#ffff"
                isClearable={true}
                options={
                  productsList && productsList?.length
                    ? productsList?.map((val: any) => {
                        return {
                          label: (
                            <div className="d-flex align-items-center">
                              <div className="symbol symbol-30px border me-3">
                                <ImageWithFallback
                                  src={val?.media[0]?.url}
                                  className="object-fit-contain"
                                  alt=""
                                  noDefaultSize
                                />
                              </div>
                              <span className="fs-16 fw-600 text-black mb-0">
                                {val?.title}
                              </span>
                            </div>
                          ),
                          value: val?.id,
                          id: val?.id,
                          variant: val,
                          title: val?.title,
                        };
                      })
                    : []
                }
                value={selectedProduct}
                onChange={(event: any) => handleProductSelect(event)}
                onMenuClose={() => onMenuClose()}
                onMenuOpen={() => onMenuOpen()}
                isDisabled={productLoading}
                hideSelectedOptions={true}
              />
            </div>
          </div>
        </Col>
        <Col md="auto">
          <div className="d-flex align-items-center">
            <FormLabel className="fs-16 fw-500">Filter by dates</FormLabel>
            <div className="ms-5">
              <CustomDatePicker
                className="form-control bg-white min-h-60px fs-16 fw-bold text-dark min-w-md-288px min-w-175px"
                onChange={handleChange}
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                dateFormat="dd/MM/yyyy"
                showFullMonthYearPicker={true}
                maxDate={new Date()}
                inputTextBG="bg-white"
              />
            </div>
          </div>
        </Col>
        <Col md="auto">
          {isAllowedToDownload && (
            <button
              className="uw-btn uw-btn-primary btn btn-primary fs-14 fw-bold px-6 py-3 h-100 d-flex align-items-center"
              onClick={handleExportExcel}
              disabled={fetchLoading || isExporting}
              data-analytics-event="finance_reports_finance_delivered_report_download_clicked"
            >
              <i className="bi bi-file-earmark-excel fs-4 me-2"></i>
              Download Report
            </button>
          )}
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className="mt-5 g-5">
        <Col xl={4} lg={6}>
          <Card className="border border-warning border-dashed bg-light-warning bg-opacity-25 shadow-sm h-100">
            <Card.Header className="min-h-50px px-6 py-3 border-bottom border-warning border-opacity-25">
              <div className="card-title m-0 d-flex align-items-center">
                <span className="fs-15 fw-bolder text-warning text-uppercase">
                  Total Delivered Summary
                </span>
              </div>
            </Card.Header>
            <Card.Body className="d-flex justify-content-between flex-wrap align-items-center p-6 gap-3">
              <div className="d-flex flex-column flex-grow-1">
                <span className="fs-13 fw-bold text-gray-500 mb-1">
                  INCL VAT
                </span>
                <span className="fs-22 fw-bolder text-dark">
                  TSh {Method.getGeneralizedAmount(totalDeliveredInclVAT)}
                </span>
              </div>
              <div className="d-flex flex-column flex-grow-1 border-start border-warning border-opacity-25 ps-5">
                <span className="fs-13 fw-bold text-gray-500 mb-1">
                  EXCL VAT
                </span>
                <span className="fs-22 fw-bolder text-dark">
                  TSh {Method.getGeneralizedAmount(totalDeliveredExclVAT)}
                </span>
              </div>
              <div className="d-flex flex-column flex-grow-1 border-start border-warning border-opacity-25 ps-5">
                <span className="fs-13 fw-bold text-gray-500 mb-1">
                  VAT AMOUNT
                </span>
                <span className="fs-22 fw-bolder text-warning">
                  TSh {Method.getGeneralizedAmount(totalDeliveredVAT)}
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Table Section */}
      <Card className="border border-r10px mt-6">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <table className="table table-row-bordered datatable align-middle gs-7 gy-4 my-0">
              <thead>
                {/* Visual Header Grouping */}
                <tr className="fs-13 fw-bolder text-uppercase bg-light text-gray-700 border-bottom border-gray-200">
                  <th className="min-w-250px p-4 border-end">
                    Product Summary
                  </th>
                  <th
                    colSpan={2}
                    className="text-center p-4 bg-light-primary  border-opacity-25"
                  >
                    Supply Quantities
                  </th>
                  <th
                    colSpan={3}
                    className="text-center p-4 bg-light-info  border-opacity-25"
                  >
                    Unit Pricing
                  </th>
                  <th
                    colSpan={3}
                    className="text-center p-4 border-end border-warning border-opacity-25"
                    style={{ backgroundColor: "#FFF5E6", color: "#FFCC00" }}
                  >
                    Delivered Totals
                  </th>
                </tr>
                <tr className="fs-12 fw-bold h-50px align-middle text-muted bg-light bg-opacity-50">
                  <th className="min-w-250px border-end px-4">
                    PRODUCT NAME & ORDER ID - CUSTOMER NAME
                  </th>
                  <th className="min-w-100px text-center border-start border-primary border-opacity-25">
                    TOTAL QTY
                  </th>
                  <th className="min-w-100px text-center border-end border-primary border-opacity-25">
                    DELIVERED QTY
                  </th>
                  <th className="min-w-125px text-end">INCL. VAT</th>
                  <th className="min-w-125px text-end">EXCL. VAT</th>
                  <th className="min-w-125px text-end border-end border-info border-opacity-25">
                    VAT / UNIT
                  </th>
                  <th className="min-w-150px text-end">TOTAL INCL. VAT</th>
                  <th className="min-w-150px text-end">TOTAL EXCL. VAT</th>
                  <th className="min-w-150px text-end border-end border-success border-opacity-25 opacity-75">
                    VAT AMOUNT
                  </th>
                </tr>
              </thead>
              <tbody className="fs-14 fw-600">
                {fetchLoading ? (
                  <tr>
                    <td colSpan={16}>
                      <div className="d-flex justify-content-center py-5">
                        <Loader loading={fetchLoading} />
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {paymentData?.length ? (
                      paymentData.map((val: any, index: number) => {
                        const uom =
                          val?.quantityType === CartonWithDozens ||
                          val?.quantityType === CartonWithPieces
                            ? "C"
                            : val?.quantityType === Dozen
                            ? "D"
                            : val?.quantityType === Piece
                            ? "P"
                            : "";
                        const totalQty = val?.totalQuantities || 0;
                        const returnedQty = val?.totalReturnedQuantities || 0;
                        const deliveredQty =
                          val?.totalReturnedQuantities && val?.totalQuantities
                            ? val?.totalQuantities -
                              val?.totalReturnedQuantities
                            : val?.totalQuantities
                            ? val?.totalDelivered
                            : 0;

                        const rowNetInclVAT =
                          (val?.totalAmount || 0) -
                          returnedQty * (val?.averagePrice || 0);
                        const rowNetExclVAT =
                          (val?.totalTaxAmount || 0) -
                          returnedQty * (val?.averageTaxPrice || 0);
                        const rowNetVAT =
                          (val?.tax || 0) -
                          returnedQty * (val?.taxPerUnit || 0);
                        const isPositive = rowNetInclVAT >= 0;

                        return (
                          <tr
                            key={index}
                            className="align-middle border-bottom border-gray-200 hover-bg-light transition"
                          >
                            {/* Product info */}
                            <td className="border-end px-4 py-3">
                              <div className="d-flex align-items-center">
                                <div className="symbol symbol-45px border me-3 bg-white rounded shadow-sm">
                                  <ImageWithFallback
                                    src={val?.variant?.media[0]?.url}
                                    className="object-fit-contain p-1 rounded"
                                    alt=""
                                    noDefaultSize
                                  />
                                </div>
                                <div className="d-flex flex-column text-nowrap">
                                  <span className="fs-14 fw-bolder text-dark mb-1">
                                    {val?.variant?.title?.replace(
                                      /\s*\)\s*/g,
                                      ")"
                                    )}
                                  </span>
                                  <span className="fs-12 fw-600 text-muted">
                                    #{val?.refKey} - {val?.name}
                                  </span>
                                </div>
                              </div>
                            </td>

                            {/* Quantities */}
                            <td className="text-center font-monospace fw-600 border-start border-primary border-opacity-10 bg-light-primary bg-opacity-25">
                              {totalQty} <span className=" fs-12">{uom}</span>
                            </td>

                            <td className="text-center font-monospace fw-bolder text-success border-end border-primary border-opacity-10 bg-light-primary bg-opacity-25">
                              {deliveredQty}{" "}
                              <span className=" fs-12">{uom}</span>
                            </td>

                            {/* Unit Pricing */}
                            <td className="text-end fw-600 pe-3 text-nowrap">
                              <span className="text-muted fs-11 me-1 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(val?.averagePrice)}
                            </td>
                            <td className="text-end fw-600 pe-3 text-nowrap">
                              <span className="text-muted fs-11 me-1 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(
                                val?.averageTaxPrice
                              )}
                            </td>
                            <td className="text-end fw-600 pe-3 border-end border-info border-opacity-10 text-nowrap bg-light-info bg-opacity-25">
                              <span className="text-info fs-11 me-1 opacity-75">
                                TSh{" "}
                                {Method.getGeneralizedAmount(val?.taxPerUnit)}
                              </span>
                            </td>

                            {/* Delivered Totals */}
                            <td className="text-end fw-bolder text-dark pe-3 text-nowrap border-start border-warning border-opacity-10">
                              <span className="text-muted fs-11 me-1 fw-600 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(val?.totalAmount)}
                            </td>
                            <td className="text-end fw-bolder text-dark pe-3 text-nowrap">
                              <span className="text-muted fs-11 me-1 fw-600 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(val?.totalTaxAmount)}
                            </td>
                            <td
                              className="text-end fw-bolder pe-3 border-end border-warning border-opacity-10 text-nowrap"
                              style={{ color: "#FF8C00" }}
                            >
                              <span className="fs-11 me-1 fw-600 opacity-75">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(val?.tax)}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={16}>
                          <div className="w-100 fs-15 py-8 fw-bold d-flex justify-content-center text-muted">
                            No Data found
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default FinanceDeliveryReport;
